import React, { useCallback, useEffect, useState } from 'react'
import { defaultDateFormat } from '@/utils/dateUtilities'
import moment from 'moment'
import { areEditorPropsEqual, InputEditorFunc } from '@/components/report-tables/editors/fInputEditor'

const dateRegExp = /^(0?\d?|[12]?\d?|3?[01]?)\/(0?[1-9]?|1?[0-2]?)\/([012]?\d{0,3})$/
const fullDateRegExp = /^(0?\d|[12]\d|3[01])\/(0?[1-9]|1[0-2])\/(\d{1,4})$/
const dayRegExp = /^(0\d|[12]\d|3[01])$/
const monthRegExp = /^(0[1-9]|1[0-2])$/
const allowedSymbolsRegExp = /^(\d|\/)*$/
const onlySeparatorsRegExp = /^\/\/$/

const separatorDeletedHandle = (value, event) => {
  const cursorLocation = event.target.selectionStart
  const twoSeparatorsPresentExp = /.*\/.*\/.*/
  if (!twoSeparatorsPresentExp.test(value)) {
    // You are welcome to do this declarative
    for (let i = cursorLocation - 1; i >= 0; i--) {
      if (value[i] !== '/') {
        event.target.setSelectionRange(i, i)
        return value.substring(0, i) + '/' + value.substring(i + 1)
      }
    }
  }
  return value
}

const DateTimeEditorFunc = React.memo(function DateTimeEditorFunc ({
  onChange = () => {},
  ...props }) {
  const [dayMatched, setDayMatched] = useState(true)
  const [monthMatched, setMonthMatched] = useState(true)

  const getPersistenceValue = useCallback((value) => {
    if (fullDateRegExp.test(value)) {
      const momentValue = moment.utc(value, defaultDateFormat)
      return momentValue.isValid() ? momentValue.toISOString() : 'invalid'
    }
    if (!value || onlySeparatorsRegExp.test(value)) {
      return null
    }
    // null means the value is refused and the selected cell won't change
    return 'invalid'
  }, [])

  const onInputChange = useCallback((event, showError) => {
    let newValue

    if (event.target.value === '') {
      setDayMatched(false)
      setMonthMatched(false)
      onChange({ value: '', start: event.target.selectionStart, end: event.target.selectionEnd })
    }

    // Adding date part divisions
    if (event.target.value && event.target.value.length === 1 && event.target.value[0] !== '/') {
      newValue = `${event.target.value}//`
      onChange({ value: newValue, start: 1, end: 1 })
      event.target.value = newValue
      event.target.setSelectionRange(1, 1)
    } else {
      newValue = event.target.value
    }

    newValue = separatorDeletedHandle(newValue, event)
    const found = newValue.match(dateRegExp)

    if (!found) {
      showError()
    } else {
      let daySet = dayMatched
      let monthSet = monthMatched

      const day = found[1]
      const month = found[2]

      if (!daySet && dayRegExp.test(day)) {
        event.target.setSelectionRange(3, 3)
        daySet = true
      }
      if (daySet && !dayRegExp.test(day)) {
        daySet = false
      }

      if (!monthSet && monthRegExp.test(month)) {
        event.target.setSelectionRange(6, 6)
        monthSet = true
      }
      if (monthSet && !monthRegExp.test(month)) monthSet = false

      onChange({ value: newValue, start: event.target.selectionStart, end: event.target.selectionEnd })
      setDayMatched(daySet)
      setMonthMatched(monthSet)
    }
  }, [dayMatched, monthMatched, onChange])

  useEffect(() => {

  }, [])

  const getValue = useCallback((value, char) => {
    let returnObject = { value: '//', start: 0, end: 0, persistTransform: getPersistenceValue }
    if ((!value && !char) || value === 'Invalid date') {
      return returnObject
    }
    let formattedTime
    if (value.length === 0 && char) {
      returnObject = { ...returnObject, start: 1, end: 1 }
      formattedTime = `${char}//`
    } else {
      let time = moment.utc(value)
      if (!time.isValid()) {
        const valueShortened = value.slice(0, value.length - 1)
        time = moment.utc(valueShortened)
        if (!time.isValid()) {
          if (fullDateRegExp.test(value)) {
            time = moment(value, defaultDateFormat)
          } else if (fullDateRegExp.test(valueShortened)) {
            time = moment(valueShortened, defaultDateFormat)
          }
        }
      }
      formattedTime = time.format(defaultDateFormat)
    }

    return (allowedSymbolsRegExp.test(formattedTime))
      ? { ...returnObject, value: formattedTime }
      : { ...returnObject, value: '//', start: 0, end: 0 }
  }, [getPersistenceValue])

  return <InputEditorFunc onInputChange={onInputChange} getValue={getValue} {...props} />
}, areEditorPropsEqual)

export { DateTimeEditorFunc }



// WEBPACK FOOTER //
// ./src/components/report-tables/editors/fDateTimeEditor.js